// 首访/6h 自动测速选最快(静默,无任何 UI)。
// 设计:docs/superpowers/specs/2026-07-13-resource-node-switch-client-design.md §3.3
// 测速复用 measureAndReportAll(portal 同款 1..4.webp 顺序累加),顺带 beacon 回传喂后端健康度。
import { getSupabase } from '@dogdisk/shared'
import { clearToAuto, isStale, setPref } from './resource-node-pref'
import { measureAndReportAll } from './resource-speedtest'

export type ResourceNodeRow = {
  id: string
  domain: string
  region: string | null
  sort_order: number | null
}

/** v_resource_node_public 全量 active 节点(与 useResourceNodes 共用) */
export async function fetchResourceNodes(): Promise<ResourceNodeRow[]> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('v_resource_node_public')
    .select('id, domain, region, sort_order')
    .order('sort_order', { ascending: true })
  if (error) throw error
  return (data ?? []) as ResourceNodeRow[]
}

export const SAMPLE_SIZE = 10

/** Fisher-Yates 洗牌取前 n(拷贝,不改入参) */
export function sampleNodes<T>(items: readonly T[], n: number): T[] {
  const a = [...items]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j]!, a[i]!]
  }
  return a.slice(0, n)
}

let running = false

/** 一轮自动选择:随机 ≤10 测速 → ok 最小 ms 写缓存;全失败回自动档;取数失败静默跳过 */
export async function runAutoSelect(now: () => number = Date.now): Promise<void> {
  if (running) return
  running = true
  try {
    let nodes: ResourceNodeRow[]
    try {
      nodes = await fetchResourceNodes()
    } catch {
      return // 本轮跳过,tested_at 不动(下个检查周期重试)
    }
    const targets = sampleNodes(
      nodes.filter((n) => !!n.domain),
      SAMPLE_SIZE,
    )
    if (targets.length === 0) return
    const results = await measureAndReportAll(targets.map((n) => n.domain))
    const okResults = results.filter((r) => r.ok && isFinite(r.ms))
    if (okResults.length === 0) {
      clearToAuto(now()) // 探测能发出说明当前域名网络可用,回退安全
      return
    }
    const fastest = okResults.reduce((a, b) => (b.ms < a.ms ? b : a))
    const winner = targets.find((n) => n.domain === fastest.domain)
    if (winner) {
      setPref({ node_id: winner.id, domain: winner.domain, tested_at: now() })
    }
  } finally {
    running = false
  }
}

export const CHECK_INTERVAL_MS = 10 * 60_000
let schedulerStarted = false

/** main.tsx 启动时调用一次:立即检查 + 每 10min 检查 staleness(≥6h 才真正测速) */
export function startAutoNodeScheduler(): void {
  if (schedulerStarted || typeof window === 'undefined') return
  schedulerStarted = true
  const tick = () => {
    if (isStale()) void runAutoSelect()
  }
  tick()
  window.setInterval(tick, CHECK_INTERVAL_MS)
}
